import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { DashboardLayout } from "../../components/DashboardLayout";
import api from "../../lib/api";
import { links } from "./SuperAdminPages";
import { Plus, Edit3, Copy, Archive, Eye, EyeOff, Save, ChevronLeft, Sparkles, Lock, Unlock, AlertTriangle, Star, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";

// ================= LIST =================
export function PricingPlansListPage() {
  const [plans, setPlans] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const nav = useNavigate();

  const refresh = React.useCallback(() => {
    api.get("/plans", { params: { include_archived: includeArchived } })
      .then((r) => setPlans(r.data));
  }, [includeArchived]);

  useEffect(() => { refresh(); }, [refresh]);

  const setStatus = async (planId, status) => {
    try { await api.patch(`/plans/${planId}/status`, null, { params: { status } }); toast.success(`Plan marked ${status}`); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setVisibility = async (planId, visibility) => {
    try { await api.patch(`/plans/${planId}/visibility`, null, { params: { visibility } }); toast.success(`Visibility: ${visibility}`); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const clonePlan = async (planId) => {
    try { const r = await api.post(`/plans/${planId}/clone`); toast.success("Cloned"); nav(`/super-admin/plans/${r.data.plan_id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deletePlan = async (planId, name) => {
    if (!window.confirm(`Delete plan "${name}"? Only allowed if no active clinics use it.`)) return;
    try { await api.delete(`/plans/${planId}`); toast.success("Deleted"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <DashboardLayout links={links} title="Pricing Plans">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <label className="text-xs text-mg-secondary flex items-center gap-1.5">
            <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} data-testid="include-archived" /> Show archived
          </label>
        </div>
        <button onClick={() => nav("/super-admin/plans/new")} className="mg-btn-primary" data-testid="add-plan-btn">
          <Plus className="w-4 h-4" /> New plan
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
        {plans.map((p) => (
          <div key={p.plan_id} className={`mg-card relative ${p.is_recommended ? "ring-2 ring-mg-medical" : ""}`} data-testid={`plan-card-${p.plan_id}`}>
            {p.is_recommended && (
              <span className="absolute -top-3 left-4 mg-gradient-magical text-mg-navy text-[10px] uppercase tracking-wider font-bold px-3 py-1 rounded-full flex items-center gap-1">
                <Star className="w-3 h-3" /> Recommended
              </span>
            )}
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-xl font-extrabold text-mg-navy">{p.name}</h3>
                <div className="text-xs text-slate-500">{p.slug}</div>
              </div>
              <div className="flex flex-col gap-1 items-end">
                <span className={`mg-status mg-status-${p.status === "active" ? "active" : p.status === "archived" ? "cancelled" : "checked-in"}`}>{p.status}</span>
                <span className={`mg-status text-[10px] ${p.visibility === "public" ? "mg-status-confirmed" : "mg-status-checked-in"}`}>
                  {p.visibility === "public" ? <Unlock className="w-3 h-3 inline mr-0.5" /> : <Lock className="w-3 h-3 inline mr-0.5" />}
                  {p.visibility}
                </span>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2 line-clamp-2">{p.short_description}</p>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="text-2xl font-extrabold text-mg-navy">{p.currency === "INR" ? "₹" : p.currency} {Number(p.monthly_price || 0).toLocaleString()}</span>
              <span className="text-xs text-slate-500">/mo</span>
              {p.yearly_price > 0 && <span className="text-xs text-slate-400 ml-2">· ₹{Number(p.yearly_price).toLocaleString()}/yr</span>}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5 text-[10px]">
              {p.trial_enabled && p.trial_days > 0 && (
                <span className="bg-green-50 text-mg-health font-bold uppercase tracking-wider rounded-full px-2 py-0.5">{p.trial_days}d trial</span>
              )}
              {p.features?.chatbot_premium_llm?.enabled && (
                <span className="mg-gradient-magical text-mg-navy font-bold uppercase tracking-wider rounded-full px-2 py-0.5">Premium AI</span>
              )}
              {p.features?.chatbot_basic?.enabled && !p.features?.chatbot_premium_llm?.enabled && (
                <span className="bg-blue-50 text-mg-medical font-bold uppercase tracking-wider rounded-full px-2 py-0.5">Basic AI</span>
              )}
              <span className="bg-slate-100 text-slate-600 font-bold uppercase tracking-wider rounded-full px-2 py-0.5"><UsersIcon className="w-3 h-3 inline mr-0.5" /> {p.clinics_count} clinic(s)</span>
            </div>
            <div className="mt-5 flex items-center gap-2 flex-wrap">
              <Link to={`/super-admin/plans/${p.plan_id}`} className="text-xs text-mg-primary font-semibold flex items-center gap-1 hover:underline" data-testid={`edit-plan-${p.plan_id}`}>
                <Edit3 className="w-3.5 h-3.5" /> Edit
              </Link>
              <button onClick={() => clonePlan(p.plan_id)} className="text-xs text-slate-600 font-semibold flex items-center gap-1 hover:text-mg-medical" data-testid={`clone-plan-${p.plan_id}`}>
                <Copy className="w-3.5 h-3.5" /> Clone
              </button>
              <button
                onClick={() => setVisibility(p.plan_id, p.visibility === "public" ? "private" : "public")}
                className="text-xs text-slate-600 font-semibold flex items-center gap-1 hover:text-mg-medical"
                data-testid={`toggle-visibility-${p.plan_id}`}
              >
                {p.visibility === "public" ? <><EyeOff className="w-3.5 h-3.5" /> Make private</> : <><Eye className="w-3.5 h-3.5" /> Make public</>}
              </button>
              {p.status !== "archived" ? (
                <button onClick={() => setStatus(p.plan_id, "archived")} className="text-xs text-amber-700 font-semibold flex items-center gap-1 ml-auto" data-testid={`archive-plan-${p.plan_id}`}>
                  <Archive className="w-3.5 h-3.5" /> Archive
                </button>
              ) : (
                <button onClick={() => setStatus(p.plan_id, "active")} className="text-xs text-mg-health font-semibold flex items-center gap-1 ml-auto" data-testid={`activate-plan-${p.plan_id}`}>
                  Activate
                </button>
              )}
              {p.clinics_count === 0 && (
                <button onClick={() => deletePlan(p.plan_id, p.name)} className="text-xs text-red-500 font-semibold ml-1" data-testid={`delete-plan-${p.plan_id}`}>
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

// ================= EDITOR =================
const EMPTY_AI = {
  monthly_token_limit: 0, daily_token_limit: 0,
  monthly_message_limit: 0, daily_message_limit: 0,
  hard_stop_enabled: true,
  allowed_roles: ["clinic_admin", "doctor", "receptionist"],
  overage_allowed: false, overage_price_per_1k_tokens: null,
};

export function PricingPlanEditorPage({ mode }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [catalog, setCatalog] = useState({ categories: {}, all_keys: [] });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", slug: "", short_description: "", description: "",
    monthly_price: 0, yearly_price: 0, currency: "INR",
    billing_cycle_options: ["monthly"],
    trial_enabled: false, trial_days: 0, trial_requires_payment_method: false,
    trial_auto_convert_to_paid: false, trial_expiry_action: "suspend",
    visibility: "public", is_recommended: false, display_order: 100, status: "active",
    features: {}, ai: EMPTY_AI, limits: {}, support_level: "",
  });

  const hydrateEditForm = useCallback((p) => ({
    name: p.name || "",
    slug: p.slug || "",
    short_description: p.short_description || "",
    description: p.description || "",
    monthly_price: p.monthly_price ?? 0,
    yearly_price: p.yearly_price ?? 0,
    currency: p.currency || "INR",
    billing_cycle_options: p.billing_cycle_options || ["monthly"],
    trial_enabled: p.trial_enabled || false,
    trial_days: p.trial_days || 0,
    trial_requires_payment_method: p.trial_requires_payment_method || false,
    trial_auto_convert_to_paid: p.trial_auto_convert_to_paid || false,
    trial_expiry_action: p.trial_expiry_action || "suspend",
    visibility: p.visibility || "public",
    is_recommended: p.is_recommended || false,
    display_order: p.display_order ?? 100,
    status: p.status || "active",
    features: p.features || {},
    ai: { ...EMPTY_AI, ...(p.ai || {}) },
    limits: p.limits || {},
    support_level: p.support_level || "",
  }), []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const catalogRes = await api.get("/plans/catalog/features");
      if (mounted) setCatalog(catalogRes.data);
      if (mode === "edit" && id) {
        const planRes = await api.get(`/plans/${id}`);
        if (mounted) setForm(hydrateEditForm(planRes.data));
      }
    })().catch((e) => {
      toast.error(e?.response?.data?.detail || "Failed to load plan data");
    });
    return () => { mounted = false; };
  }, [mode, id, hydrateEditForm]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFeature = (key, patch) => setForm((f) => ({
    ...f,
    features: { ...f.features, [key]: { ...(f.features[key] || { enabled: false, limit: null }), ...patch } },
  }));
  const setAi = (k, v) => setForm((f) => ({ ...f, ai: { ...f.ai, [k]: v } }));
  const setLimit = (k, v) => setForm((f) => ({ ...f, limits: { ...f.limits, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (mode === "edit") await api.put(`/plans/${id}`, body);
      else await api.post("/plans", body);
      toast.success("Plan saved");
      nav("/super-admin/plans");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  return (
    <DashboardLayout links={links} title={mode === "edit" ? "Edit pricing plan" : "Create pricing plan"}>
      <div className="flex items-center gap-2 mb-4">
        <Link to="/super-admin/plans" className="text-xs text-mg-muted hover:text-mg-medical flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Back to plans</Link>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        {/* Left: basic + pricing + trial */}
        <div className="xl:col-span-1 space-y-5">
          <Section title="Basic details">
            <Field label="Plan name"><input value={form.name} onChange={(e) => set("name", e.target.value)} className="mg-input" data-testid="plan-name-input" /></Field>
            <Field label="Slug (optional)"><input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto-generated from name" className="mg-input" data-testid="plan-slug-input" /></Field>
            <Field label="Short description"><textarea value={form.short_description} onChange={(e) => set("short_description", e.target.value)} className="mg-input" rows={2} data-testid="plan-short-desc" /></Field>
            <Field label="Detailed description"><textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mg-input" rows={3} data-testid="plan-desc" /></Field>
            <Field label="Display order"><input type="number" value={form.display_order} onChange={(e) => set("display_order", Number(e.target.value))} className="mg-input" data-testid="plan-display-order" /></Field>
          </Section>
          <Section title="Pricing">
            <Field label="Currency"><select value={form.currency} onChange={(e) => set("currency", e.target.value)} className="mg-input"><option value="INR">INR</option><option value="USD">USD</option><option value="EUR">EUR</option></select></Field>
            <Field label="Monthly price"><input type="number" value={form.monthly_price} onChange={(e) => set("monthly_price", Number(e.target.value))} className="mg-input" data-testid="plan-monthly-price" /></Field>
            <Field label="Yearly price (optional)"><input type="number" value={form.yearly_price || 0} onChange={(e) => set("yearly_price", Number(e.target.value))} className="mg-input" data-testid="plan-yearly-price" /></Field>
          </Section>
          <Section title="Capacity limits">
            <Field label="Active branches">
              <input type="number" min="1" value={form.limits.branches ?? ""}
                onChange={(e) => setLimit("branches", e.target.value === "" ? null : Number(e.target.value))}
                className="mg-input" placeholder="Blank = unlimited" data-testid="plan-branch-limit" />
            </Field>
            <p className="text-xs text-slate-500">Creation and reactivation stop at this limit. Reducing a plan never silently deletes or deactivates existing branches.</p>
          </Section>
          <Section title="Trial">
            <label className="flex items-center gap-2 text-sm text-mg-navy mt-1">
              <input type="checkbox" checked={form.trial_enabled} onChange={(e) => set("trial_enabled", e.target.checked)} data-testid="plan-trial-enabled" /> Trial enabled
            </label>
            <Field label="Trial days"><input type="number" value={form.trial_days} onChange={(e) => set("trial_days", Number(e.target.value))} disabled={!form.trial_enabled} className="mg-input disabled:opacity-50" data-testid="plan-trial-days" /></Field>
            <Field label="Trial expiry action">
              <select value={form.trial_expiry_action} onChange={(e) => set("trial_expiry_action", e.target.value)} disabled={!form.trial_enabled} className="mg-input disabled:opacity-50" data-testid="plan-trial-expiry">
                <option value="suspend">Suspend clinic</option>
                <option value="downgrade">Downgrade to free</option>
                <option value="keep_active_pending_payment">Keep active, mark past_due</option>
              </select>
            </Field>
            <label className="flex items-center gap-2 text-xs text-slate-600 mt-1">
              <input type="checkbox" checked={form.trial_requires_payment_method} onChange={(e) => set("trial_requires_payment_method", e.target.checked)} disabled={!form.trial_enabled} /> Require card to start trial
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-600 mt-1">
              <input type="checkbox" checked={form.trial_auto_convert_to_paid} onChange={(e) => set("trial_auto_convert_to_paid", e.target.checked)} disabled={!form.trial_enabled} /> Auto-convert trial to paid
            </label>
          </Section>
          <Section title="Visibility & status">
            <Field label="Visibility">
              <select value={form.visibility} onChange={(e) => set("visibility", e.target.value)} className="mg-input" data-testid="plan-visibility">
                <option value="public">Public (shown on landing)</option>
                <option value="private">Private (manual assign only)</option>
              </select>
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className="mg-input" data-testid="plan-status">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="archived">Archived</option>
              </select>
            </Field>
            <label className="flex items-center gap-2 text-sm text-mg-navy mt-1">
              <input type="checkbox" checked={form.is_recommended} onChange={(e) => set("is_recommended", e.target.checked)} data-testid="plan-recommended" /> Mark as recommended / popular
            </label>
          </Section>
        </div>
        {/* Middle: features */}
        <div className="xl:col-span-1 space-y-5">
          <Section title="Business areas & features">
            <p className="text-xs text-slate-500 mb-2">Toggle which modules and features are included in this plan. AI features below are gated separately.</p>
            {Object.entries(catalog.categories || {}).map(([cat, items]) => (
              <div key={cat} className="mb-4">
                <div className="text-[10px] uppercase tracking-wider font-bold text-mg-medical mb-2">{cat}</div>
                <div className="space-y-1.5">
                  {items.map((f) => {
                    const v = form.features[f.key] || { enabled: false, limit: null };
                    return (
                      <label key={f.key} className="flex items-center gap-2 text-xs text-slate-700">
                        <input type="checkbox" checked={!!v.enabled} onChange={(e) => setFeature(f.key, { enabled: e.target.checked })} data-testid={`feature-${f.key}`} />
                        <span className="flex-1">{f.name}</span>
                        {f.has_limit && v.enabled && (
                          <input
                            type="number" value={v.limit ?? ""} onChange={(e) => setFeature(f.key, { limit: e.target.value === "" ? null : Number(e.target.value) })}
                            placeholder="Limit" className="w-20 text-xs border border-slate-200 rounded px-1.5 py-0.5"
                          />
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </Section>
        </div>
        {/* Right: AI + save */}
        <div className="xl:col-span-1 space-y-5">
          <Section title="AI / Chatbot access">
            <p className="text-xs text-slate-500 mb-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <span>Premium LLM calls are <strong>never</strong> made before checking these limits. Set <strong>chatbot_premium_llm</strong> in the Features panel and configure limits here.</span>
            </p>
            <Field label="Monthly token limit"><input type="number" value={form.ai.monthly_token_limit ?? ""} onChange={(e) => setAi("monthly_token_limit", e.target.value === "" ? null : Number(e.target.value))} className="mg-input" placeholder="0 = unlimited" data-testid="ai-monthly-token-limit" /></Field>
            <Field label="Daily token limit"><input type="number" value={form.ai.daily_token_limit ?? ""} onChange={(e) => setAi("daily_token_limit", e.target.value === "" ? null : Number(e.target.value))} className="mg-input" placeholder="0 = unlimited" /></Field>
            <Field label="Monthly message limit"><input type="number" value={form.ai.monthly_message_limit ?? ""} onChange={(e) => setAi("monthly_message_limit", e.target.value === "" ? null : Number(e.target.value))} className="mg-input" /></Field>
            <Field label="Daily message limit"><input type="number" value={form.ai.daily_message_limit ?? ""} onChange={(e) => setAi("daily_message_limit", e.target.value === "" ? null : Number(e.target.value))} className="mg-input" /></Field>
            <label className="flex items-center gap-2 text-xs text-slate-700 mt-1">
              <input type="checkbox" checked={form.ai.hard_stop_enabled} onChange={(e) => setAi("hard_stop_enabled", e.target.checked)} data-testid="ai-hard-stop" /> Hard stop when limit reached
            </label>
          </Section>
          <div className="mg-card sticky top-4">
            <h3 className="font-bold text-mg-navy text-sm">Ready to save?</h3>
            <p className="text-xs text-slate-500 mt-1">Changes apply immediately. Clinics on this plan will get the new features on their next request.</p>
            <button onClick={save} disabled={saving || !form.name} className="mg-btn-primary w-full mt-4 justify-center" data-testid="save-plan-btn">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save plan"}
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Section({ title, children }) {
  return (
    <div className="mg-card">
      <h3 className="font-bold text-mg-navy text-sm mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-mg-medical" /> {title}</h3>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider font-bold text-mg-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
